/**
 * buildWebBleTicket.ts — Bytes ESC/POS de un ticket de pedido del panel.
 *
 * Desde la UNIFICACIÓN del builder, el CONTENIDO ya no vive aquí: lo centraliza el IR
 * `buildTicketModel(order, config)` (ticketModel.ts). Este módulo es el EMITTER de bytes
 * sobre ese IR: `emitEscPos(doc, sizeCfg)` materializa la secuencia ESC/POS (align/bold/
 * size/line/row/sep/feed/cut) EN EL MISMO ORDEN que el legacy, y `buildWebBleTicket`
 * es el wrapper público (order, options) → bytes que arma la config y llama al emitter.
 *
 * BYTE-SAFETY (RESTRICCIÓN DURA): los bytes están VALIDADOS EN HARDWARE (PT-220). El
 * emitter reproduce comando-a-comando lo que antes hacían buildNormalTicket/
 * buildSimplifiedTicket. La equivalencia byte-a-byte está congelada por un golden
 * (__tests__/ticketModel.bytes.test.ts) sobre una matrix que ejercita todas las ramas.
 *
 *   - `ticketType: 'normal'`     -> ticket de caja (productos, extras, totales, pago,
 *                                   domicilio, footer del comercio).
 *   - `ticketType: 'simplified'` -> comanda de cocina SIN precios.
 *
 * Logo: el RASTER lo resuelve el caller (useWebBlePrint) y se pasa en options.logoRaster;
 * el IR solo marca {kind:'logo'} y emitEscPos lo materializa (GS v 0) si hay raster.
 */

import { OrderDetail } from '@interfaces/OrderTypes';
import { PRINT_TEXT_SIZE_MAP, TICKET_TYPE_MAP } from '@utils/constants';
import type { TicketContentFlags } from '@store/slices/webBlePrinters.types';
import { EscPos, EscPosSize } from './escpos';
import { buildTicketModel, TicketContentConfig, TicketDoc } from './ticketModel';

// Re-export de helpers de dominio (única copia vive en ticketModel.ts) para no romper los
// imports existentes (textLayout.ts, tests, consumidores).
export { computeOrderTotal, LEGAL_DISCLAIMER, paymentLabel } from './ticketModel';
export type { TicketContentConfig, TicketDoc } from './ticketModel';

export type WebBleTicketType = keyof typeof TICKET_TYPE_MAP; // 'normal' | 'simplified'

export type WebBleTicketOptions = {
  /** Tipo de ticket: completo con precios o comanda de cocina simplificada. */
  ticketType: WebBleTicketType;
  /** Tamaño de texto del cuerpo (mapea a doble-alto en encabezados). */
  textSize?: keyof typeof PRINT_TEXT_SIZE_MAP;
  /** Datos del comercio para el footer (sólo ticket normal). */
  commerceName?: string;
  commerceAddress?: string;
  /** Etiqueta personalizada para el campo de referencias de dirección. */
  referencesLabel?: string;
  /** Flags de visibilidad — paridad con el ticket Electron. */
  showClientPhone?: boolean;
  /** Imprimir el NOMBRE del comercio. Aplica a caja Y cocina. Default true. */
  showCommerceName?: boolean;
  /** Imprimir la DIRECCIÓN del comercio (solo caja). Default true. */
  showCommerceAddress?: boolean;
  /** Líneas en blanco antes del corte (papel para arrancar el ticket). */
  feedBeforeCut?: number;
  /** Ancho del rollo en mm — define el ancho de las líneas separadoras. */
  pageWidth?: 58 | 80;
  /** Si es false, omite las líneas separadoras decorativas. */
  decoration?: boolean;
  /** Comando ESC/POS raster del logo (GS v 0) ya resuelto; se imprime arriba. */
  logoRaster?: Uint8Array | null;
  /**
   * Flags de contenido RESUELTOS (showPrices/showTotals/showTip/…). OPCIONAL: cuando se
   * omite, el IR usa el preset del rol (ticketType) + los show* legacy → salida byte-idéntica
   * histórica. Cuando se pasa (config rica de la UI), el IR honra estos flags.
   */
  content?: TicketContentFlags;
};

// Caracteres por línea según el ancho del rollo (aprox. fuente A: 32 @58mm, 48 @80mm).
// Se usa para el WRAP de texto/columnas (NO para el separador). Exportado para que el
// ESPEJO de texto (textLayout.ts) maquete con el MISMO ancho de columna que la impresión.
export const bodyWidth = (options: { pageWidth?: 58 | 80 }): number => (options.pageWidth === 58 ? 32 : 48);

// Ancho CONSERVADOR del separador en columnas (columnas − 1). Imprimir el ancho EXACTO
// de columna provoca auto-wrap en muchas térmicas → el separador sale como línea doble.
// Bajamos a columnas−1 (58mm→31, 80mm→47) para dejar margen seguro. Exportado para que
// el preview use EXACTAMENTE el mismo valor que la impresión y no diverja.
export const separatorCols = (pageWidth?: 58 | 80): number => (pageWidth === 58 ? 31 : 47);

// Mapea el tamaño de texto seleccionado al modo de tamaño ESC/POS para los
// encabezados. Las térmicas no tienen escala de px; usamos doble-alto+ancho
// para los títulos en tamaños grandes y normal en los pequeños.
export function titleSize(textSize: keyof typeof PRINT_TEXT_SIZE_MAP | undefined): EscPosSize {
  switch (textSize) {
    case 'large':
    case 'xxl':
      return 3; // doble alto + ancho
    case 'medium':
      return 1; // doble alto
    default:
      return 0; // normal (xxs / small / undefined)
  }
}

// Tamaño del CUERPO del ticket (no solo el título), para que "Tamaño del texto" afecte
// TODO el ticket en modo texto. Se usa SOLO doble-ALTO (mode 1), nunca doble-ancho: el
// ancho de carácter se mantiene, así que el conteo de columnas de `row`/`sepLine` (32/48)
// sigue siendo válido y las líneas no se desbordan. Las térmicas no tienen escala de px,
// por eso solo hay 2 pasos reales (normal / doble-alto). Para escala fiel por elemento el
// usuario elige el MODO IMAGEN ('Como en la app'), que sí soporta los 5 niveles.
export function bodySize(textSize: keyof typeof PRINT_TEXT_SIZE_MAP | undefined): EscPosSize {
  switch (textSize) {
    case 'large':
    case 'xxl':
      return 1; // doble alto en todo el cuerpo
    default:
      return 0; // normal (xxs / small / medium / undefined)
  }
}

// Configuración de tamaño/decoración/logo que el emitter de bytes necesita para
// materializar el IR (lo que el IR no captura por ser presentación, no contenido).
export type EmitEscPosSizeConfig = {
  /** Ancho del separador en columnas (separatorCols). */
  separatorCols: number;
  /** Raster del logo ya resuelto (GS v 0); null/undefined → no se imprime logo. */
  logoRaster?: Uint8Array | null;
};

// Fila "etiqueta ........ valor": valor alineado a la derecha en una sola línea si
// cabe; si no, etiqueta arriba y valor a la derecha debajo. Espejo EXACTO del helper
// row() histórico (mismo orden de comandos: línea izq, align 2, línea der, align 0).
const emitRow = (enc: EscPos, left: string, right: string, width: number): void => {
  if (left.length + right.length + 1 <= width) {
    enc.line(left + ' '.repeat(width - left.length - right.length) + right);
  } else {
    enc.line(left);
    enc.align(2).line(right).align(0);
  }
};

// Emitter de bytes ESC/POS sobre el IR. Reproduce comando-a-comando lo que hacían
// buildNormalTicket/buildSimplifiedTicket: por eso la salida es BYTE-IDÉNTICA al legacy.
// PROHIBIDO agregar contenido aquí — solo materializa el doc.
export function emitEscPos(doc: TicketDoc, sizeCfg: EmitEscPosSizeConfig): Uint8Array {
  const enc = new EscPos();
  for (const block of doc.blocks) {
    switch (block.kind) {
      case 'logo':
        // Espejo de writeLogo: solo imprime si hay raster (align centro, raster, feed 1, align 0).
        if (sizeCfg.logoRaster) enc.align(1).raw(sizeCfg.logoRaster).feed(1).align(0);
        break;
      case 'align':
        enc.align(block.n);
        break;
      case 'bold':
        enc.bold(block.on);
        break;
      case 'size':
        enc.size(block.mode);
        break;
      case 'line':
        enc.line(block.text);
        break;
      case 'row':
        emitRow(enc, block.left, block.right, block.width);
        break;
      case 'sep':
        // sep solo aparece en el IR cuando decoration ≠ false (buildTicketModel ya lo filtra).
        enc.line('-'.repeat(sizeCfg.separatorCols));
        break;
      case 'feed':
        enc.feed(block.n);
        break;
      case 'cut':
        enc.feed(block.feed).cut();
        break;
    }
  }
  return enc.encode();
}

// Traduce las WebBleTicketOptions (superficie pública histórica) a la TicketContentConfig
// declarativa del IR. Mantiene los defaults exactos del legacy (feedBeforeCut ?? 3, etc.).
export function buildTicketConfig(options: WebBleTicketOptions): TicketContentConfig {
  return {
    ticketType: options.ticketType,
    referencesLabel: options.referencesLabel,
    commerceName: options.commerceName,
    commerceAddress: options.commerceAddress,
    showClientPhone: options.showClientPhone,
    showCommerceName: options.showCommerceName,
    showCommerceAddress: options.showCommerceAddress,
    hasLogo: Boolean(options.logoRaster),
    content: options.content,
    titleSizeMode: titleSize(options.textSize),
    bodySizeMode: bodySize(options.textSize),
    bodyWidth: bodyWidth(options),
    decoration: options.decoration,
    feedBeforeCut: options.feedBeforeCut ?? 3,
  };
}

// Construye los bytes ESC/POS del ticket para imprimir por Web Bluetooth.
// Devuelve un `Uint8Array` listo para escribir a la característica BLE.
export function buildWebBleTicket(order: OrderDetail, options: WebBleTicketOptions): Uint8Array {
  const doc = buildTicketModel(order, buildTicketConfig(options));
  return emitEscPos(doc, {
    separatorCols: separatorCols(options.pageWidth),
    logoRaster: options.logoRaster,
  });
}
